import React, { useRef, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  Bold,
  Italic,
  Underline as UIcon,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Eye,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Minus,
  Eraser,
  Heading2,
  Heading3,
  Pilcrow,
  Undo2,
  Redo2,
  ImagePlus,
  FilmIcon,
} from "lucide-react";

const FONT_FAMILIES = [
  { label: "Default (sans)", value: "" },
  { label: "IBM Plex Sans", value: '"IBM Plex Sans", sans-serif' },
  { label: "Playfair Display (serif)", value: '"Playfair Display", Georgia, serif' },
  { label: "Cairo (Arabic)", value: '"Cairo", sans-serif' },
  { label: "Amiri (Arabic serif)", value: '"Amiri", serif' },
  { label: "Georgia", value: 'Georgia, serif' },
  { label: "Times New Roman", value: '"Times New Roman", serif' },
  { label: "Arial", value: 'Arial, sans-serif' },
  { label: "Tahoma", value: 'Tahoma, sans-serif' },
  { label: "Verdana", value: 'Verdana, sans-serif' },
  { label: "Courier New (mono)", value: '"Courier New", monospace' },
  { label: "IBM Plex Mono", value: '"IBM Plex Mono", monospace' },
];

const FONT_SIZES = [
  { label: "12", value: "12px" },
  { label: "14", value: "14px" },
  { label: "16", value: "16px" },
  { label: "18", value: "18px" },
  { label: "20", value: "20px" },
  { label: "22", value: "22px" },
  { label: "26", value: "26px" },
  { label: "32", value: "32px" },
  { label: "40", value: "40px" },
];

const TEXT_COLORS = [
  { label: "Black", value: "#0F172A" },
  { label: "Dark Gray", value: "#334155" },
  { label: "Gray", value: "#64748B" },
  { label: "Navy", value: "#1E3A8A" },
  { label: "Blue", value: "#2563EB" },
  { label: "Red", value: "#991B1B" },
  { label: "Green", value: "#166534" },
  { label: "Orange", value: "#9A3412" },
  { label: "White", value: "#FFFFFF" },
];

const HIGHLIGHT_COLORS = [
  { label: "None", value: "transparent" },
  { label: "Yellow", value: "#FEF3C7" },
  { label: "Gray", value: "#E5E7EB" },
  { label: "Red", value: "#FEE2E2" },
  { label: "Blue", value: "#DBEAFE" },
  { label: "Green", value: "#D1FAE5" },
];

export default function RichTextEditor({ value, onChange, placeholder, testId, rows = 18 }) {
  const editorRef = useRef(null);
  const [preview, setPreview] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const isUpdatingRef = useRef(false);

  // Sync external value into editor if it changes (only when editor isn't the source)
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (isUpdatingRef.current) {
      isUpdatingRef.current = false;
      return;
    }
    if (el.innerHTML !== (value || "")) {
      el.innerHTML = value || "";
    }
  }, [value]);

  const exec = (command, arg = null) => {
    // Keep focus inside the editor so the command applies to current selection
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    handleInput();
  };

  const handleInput = () => {
    isUpdatingRef.current = true;
    onChange(editorRef.current?.innerHTML || "");
  };

  // Sanitise pasted HTML — strip MS Word / Office cruft & dangerous tags.
  const cleanPastedHtml = (html) => {
    if (!html) return "";
    let h = html;
    // Strip MS Office conditional comments
    h = h.replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, "");
    // Remove <meta>, <link>, <style>, <xml>, <script>, <w:*>, <o:*>, <m:*>, <v:*> tags & their content
    h = h.replace(/<(meta|link|style|xml|script)[^>]*>[\s\S]*?<\/\1>/gi, "");
    h = h.replace(/<(meta|link|xml|script)[^>]*\/?>/gi, "");
    h = h.replace(/<\/?(o|w|m|v):[a-z0-9-]+[^>]*>/gi, "");
    // Strip MS Office class names (MsoNormal etc)
    h = h.replace(/\sclass\s*=\s*"Mso[^"]*"/gi, "");
    h = h.replace(/\sclass\s*=\s*'Mso[^']*'/gi, "");
    // Remove mso-* CSS properties
    h = h.replace(/mso-[\w-]+\s*:[^;"']+;?/gi, "");
    // Strip lang attributes
    h = h.replace(/\slang\s*=\s*"[^"]*"/gi, "");
    h = h.replace(/\sxml:lang\s*=\s*"[^"]*"/gi, "");
    // Remove empty style="" attributes that resulted from mso-cleanup
    h = h.replace(/\sstyle\s*=\s*"\s*;?\s*"/gi, "");
    // Strip MS Office font-family hints (Calibri, Traditional Arabic etc) → use default
    h = h.replace(/font-family\s*:[^;"]+;?/gi, "");
    // Collapse repeated spaces
    h = h.replace(/\s{2,}/g, " ");
    // Remove empty spans
    for (let i = 0; i < 5; i++) {
      h = h.replace(/<span[^>]*>\s*<\/span>/gi, "");
    }
    return h.trim();
  };

  // Shared image upload + insert at current cursor (used by file picker, paste & drop)
  const uploadAndInsertImage = async (file, askCaption = true) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please drop or paste an image file");
      return;
    }
    const t = toast.loading("Uploading image…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "image");
      const { data } = await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      // Use the relative path so the embedded URL works on any domain
      // (preview, production, custom domain). Storing absolute URLs would
      // permanently bind the asset to the preview host where it was uploaded.
      const fullUrl = data.public_url;
      const caption = askCaption ? (window.prompt("Caption (optional):", "") || "") : "";
      const alt = (caption || file.name || "image").replace(/"/g, "&quot;");
      const figureHtml = caption
        ? `<figure style="margin:1.5em 0;text-align:center"><img src="${fullUrl}" alt="${alt}" style="max-width:100%;height:auto;border:1px solid #E2E8F0" /><figcaption style="margin-top:0.5em;font-size:0.85em;color:#64748B;text-align:center;font-style:italic">${caption.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c]))}</figcaption></figure><p><br/></p>`
        : `<p style="text-align:center"><img src="${fullUrl}" alt="${alt}" style="max-width:100%;height:auto;border:1px solid #E2E8F0;margin:1em 0" /></p><p><br/></p>`;
      ensureCaretInEditor();
      document.execCommand("insertHTML", false, figureHtml);
      handleInput();
      toast.success("Image inserted", { id: t });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Upload failed", { id: t });
    }
  };

  // Ensure the selection is inside the editor before running execCommand.
  // Native dialogs (file picker, window.prompt) can move focus away and leave
  // the editor without a valid Range — especially when the editor is empty
  // (e.g. a fresh language tab). Without a Range inside the editor,
  // `execCommand('insertHTML')` silently fails. This places a caret at the
  // end of the editor so insertion always works.
  const ensureCaretInEditor = () => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    let inside = false;
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      inside = el.contains(r.startContainer);
    }
    if (inside) return;
    // Place caret at the very end of the editor's content (or inside if empty)
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  // Place caret at the position of a drop event so the image inserts where dropped
  const placeCaretAt = (clientX, clientY) => {
    let range = null;
    if (document.caretRangeFromPoint) {
      range = document.caretRangeFromPoint(clientX, clientY);
    } else if (document.caretPositionFromPoint) {
      const pos = document.caretPositionFromPoint(clientX, clientY);
      if (pos) {
        range = document.createRange();
        range.setStart(pos.offsetNode, pos.offset);
        range.collapse(true);
      }
    }
    const editor = editorRef.current;
    if (range && editor && editor.contains(range.startContainer)) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      ensureCaretInEditor();
    }
  };

  const handlePaste = (e) => {
    const cd = e.clipboardData || window.clipboardData;
    if (!cd) return;

    // 1) Pasted image or video file (e.g. from screenshot, copied file in OS, browser image copy)
    const items = cd.items ? Array.from(cd.items) : [];
    const imageItem = items.find((it) => it.kind === "file" && it.type.startsWith("image/"));
    if (imageItem) {
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (file) uploadAndInsertImage(file, false);
      return;
    }
    const videoItem = items.find((it) => it.kind === "file" && it.type.startsWith("video/"));
    if (videoItem) {
      e.preventDefault();
      const file = videoItem.getAsFile();
      if (file) uploadAndInsertVideoFile(file);
      return;
    }
    // Some browsers expose pasted files via clipboardData.files instead of items
    if (cd.files && cd.files.length > 0) {
      const f = cd.files[0];
      if (f.type.startsWith("image/")) {
        e.preventDefault();
        uploadAndInsertImage(f, false);
        return;
      }
      if (f.type.startsWith("video/")) {
        e.preventDefault();
        uploadAndInsertVideoFile(f);
        return;
      }
    }

    // 2) Pasted HTML — sanitise Word/Office cruft
    const html = cd.getData("text/html");
    if (html) {
      e.preventDefault();
      const clean = cleanPastedHtml(html);
      document.execCommand("insertHTML", false, clean);
      handleInput();
    }
    // Plain text falls through to default behaviour
  };

  const handleDragOver = (e) => {
    if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes("Files")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      if (!isDragOver) setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    if (e.target === editorRef.current) setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    if (!e.dataTransfer) return;
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;
    e.preventDefault();
    setIsDragOver(false);
    placeCaretAt(e.clientX, e.clientY);
    // Upload images one-by-one in dropped order; skip non-images
    for (const f of files) {
      if (f.type.startsWith("image/")) {
        // eslint-disable-next-line no-await-in-loop
        await uploadAndInsertImage(f, false);
      } else if (f.type.startsWith("video/")) {
        // eslint-disable-next-line no-await-in-loop
        await uploadAndInsertVideoFile(f);
      } else {
        toast.error(`Unsupported file: ${f.name}`);
      }
    }
  };

  // Used by both the toolbar button and the drag-drop handler for video files
  const uploadAndInsertVideoFile = async (file) => {
    if (!file) return;
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > 100) {
      const ok = window.confirm(`Video is ${sizeMB.toFixed(1)} MB. Continue?`);
      if (!ok) return;
    }
    const t = toast.loading(`Uploading video (${sizeMB.toFixed(1)} MB)…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "video");
      const { data } = await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => {
          if (p.total) {
            const pct = Math.round((p.loaded / p.total) * 100);
            toast.loading(`Uploading video… ${pct}%`, { id: t });
          }
        },
        timeout: 300000,
      });
      // Use the relative path so the embedded URL works on any domain
      // (preview, production, custom domain).
      const fullUrl = data.public_url;
      // Ask for description so AI tools and crawlers can understand the video
      const description = window.prompt(
        "Video description (recommended) — this lets ChatGPT/Google understand what the video shows:",
        ""
      ) || "";
      const safeDesc = description.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c]));
      const captionHtml = description
        ? `<figcaption style="margin-top:0.5em;font-size:0.9em;color:#334155;text-align:center;font-style:italic;line-height:1.5">${safeDesc}</figcaption>`
        : "";
      // Add data-description so the share-page renderer can extract the description as plain text for AI/crawlers
      const figureHtml = `<figure style="margin:1.5em 0;text-align:center" data-video-description="${safeDesc}"><video src="${fullUrl}" controls preload="metadata" style="max-width:100%;height:auto;border:1px solid #E2E8F0;background:#000"></video>${captionHtml}</figure><p><br/></p>`;
      ensureCaretInEditor();
      document.execCommand("insertHTML", false, figureHtml);
      handleInput();
      toast.success("Video inserted", { id: t });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Upload failed", { id: t });
    }
  };

  const setFontFamily = (family) => {
    if (!family) {
      exec("removeFormat");
      return;
    }
    // Use inline style via styleWithCSS
    document.execCommand("styleWithCSS", false, true);
    // Wrap in span with font-family
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const span = document.createElement("span");
    span.style.fontFamily = family;
    span.appendChild(range.extractContents());
    range.insertNode(span);
    sel.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    sel.addRange(newRange);
    handleInput();
  };

  const setFontSize = (size) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const span = document.createElement("span");
    span.style.fontSize = size;
    span.appendChild(range.extractContents());
    range.insertNode(span);
    sel.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    sel.addRange(newRange);
    handleInput();
  };

  const setTextColor = (color) => {
    document.execCommand("styleWithCSS", false, true);
    exec("foreColor", color);
  };

  const setHighlight = (color) => {
    document.execCommand("styleWithCSS", false, true);
    if (color === "transparent") {
      exec("hiliteColor", "transparent");
    } else {
      exec("hiliteColor", color);
    }
  };

  const addLink = () => {
    const url = window.prompt("Enter URL:", "https://");
    if (!url) return;
    exec("createLink", url);
  };

  const clearFormat = () => exec("removeFormat");

  // Insert image: upload file via storage API, then insert <figure> at cursor
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const triggerImageUpload = () => fileInputRef.current?.click();
  const triggerVideoUpload = () => videoInputRef.current?.click();
  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      await uploadAndInsertImage(file, true);
    } finally {
      setUploading(false);
    }
  };

  const handleVideoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please select a video file (mp4, webm, mov)");
      return;
    }
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > 100) {
      const ok = window.confirm(`Video is ${sizeMB.toFixed(1)} MB. Large videos may take a while to upload and load. Continue?`);
      if (!ok) return;
    }
    setUploading(true);
    setUploadProgress(0);
    const t = toast.loading(`Uploading video (${sizeMB.toFixed(1)} MB)…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "video");
      const { data } = await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => {
          if (p.total) {
            const pct = Math.round((p.loaded / p.total) * 100);
            setUploadProgress(pct);
            toast.loading(`Uploading video… ${pct}%`, { id: t });
          }
        },
        timeout: 300000,
      });
      // Relative path — works on any domain
      const fullUrl = data.public_url;
      const caption = window.prompt("Caption (optional):", "") || "";
      const captionHtml = caption
        ? `<figcaption style="margin-top:0.5em;font-size:0.85em;color:#64748B;text-align:center;font-style:italic">${caption.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c]))}</figcaption>`
        : "";
      const figureHtml = `<figure style="margin:1.5em 0;text-align:center"><video src="${fullUrl}" controls preload="metadata" style="max-width:100%;height:auto;border:1px solid #E2E8F0;background:#000"></video>${captionHtml}</figure><p><br/></p>`;
      ensureCaretInEditor();
      document.execCommand("insertHTML", false, figureHtml);
      handleInput();
      toast.success("Video inserted", { id: t });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Upload failed", { id: t });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const insertHeading = (level) => {
    exec("formatBlock", `<h${level}>`);
  };

  const insertParagraph = () => exec("formatBlock", "<p>");
  const insertQuote = () => exec("formatBlock", "<blockquote>");
  const insertHr = () => exec("insertHorizontalRule");

  // Keep the editor focused behaviour — on click outside toolbar, don't lose selection
  const preventBlur = (e) => e.preventDefault();

  return (
    <div className="border border-slate200 bg-white" data-testid={testId}>
      {/* Toolbar */}
      <div
        className="border-b border-slate200 bg-slate50 px-2 py-1.5"
        onMouseDown={preventBlur}
      >
        <div className="flex items-center gap-1 flex-wrap">
          {/* Font family */}
          <select
            onChange={(e) => { setFontFamily(e.target.value); e.target.value = ""; }}
            defaultValue=""
            className="text-[12px] border border-slate200 bg-white px-2 h-7 outline-none hover:border-ink"
            data-testid="rte-font-family"
            title="Font family"
          >
            <option value="">Font…</option>
            {FONT_FAMILIES.map((f) => (
              <option key={f.label} value={f.value} style={{ fontFamily: f.value || "inherit" }}>
                {f.label}
              </option>
            ))}
          </select>

          {/* Font size */}
          <select
            onChange={(e) => { setFontSize(e.target.value); e.target.value = ""; }}
            defaultValue=""
            className="text-[12px] border border-slate200 bg-white px-2 h-7 outline-none hover:border-ink"
            data-testid="rte-font-size"
            title="Font size"
          >
            <option value="">Size…</option>
            {FONT_SIZES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>

          <Sep />

          {/* Bold / Italic / Underline / Strike */}
          <Btn title="Bold (Ctrl+B)" onClick={() => exec("bold")} test="rte-bold"><Bold className="w-4 h-4" /></Btn>
          <Btn title="Italic (Ctrl+I)" onClick={() => exec("italic")} test="rte-italic"><Italic className="w-4 h-4" /></Btn>
          <Btn title="Underline" onClick={() => exec("underline")} test="rte-underline"><UIcon className="w-4 h-4" /></Btn>
          <Btn title="Strikethrough" onClick={() => exec("strikeThrough")} test="rte-strike"><Strikethrough className="w-4 h-4" /></Btn>

          <Sep />

          {/* Text color */}
          <ColorPicker
            label="A"
            colors={TEXT_COLORS}
            onPick={setTextColor}
            testId="rte-color"
          />
          {/* Highlight */}
          <ColorPicker
            label="H"
            colors={HIGHLIGHT_COLORS}
            onPick={setHighlight}
            testId="rte-highlight"
            highlight
          />

          <Sep />

          {/* Blocks */}
          <Btn title="Paragraph" onClick={insertParagraph} test="rte-p"><Pilcrow className="w-4 h-4" /></Btn>
          <Btn title="Heading 2" onClick={() => insertHeading(2)} test="rte-h2"><Heading2 className="w-4 h-4" /></Btn>
          <Btn title="Heading 3" onClick={() => insertHeading(3)} test="rte-h3"><Heading3 className="w-4 h-4" /></Btn>
          <Btn title="Quote" onClick={insertQuote} test="rte-quote"><Quote className="w-4 h-4" /></Btn>
          <Btn title="Horizontal line" onClick={insertHr} test="rte-hr"><Minus className="w-4 h-4" /></Btn>

          <Sep />

          {/* Alignment */}
          <Btn title="Align left" onClick={() => exec("justifyLeft")} test="rte-left"><AlignLeft className="w-4 h-4" /></Btn>
          <Btn title="Align center" onClick={() => exec("justifyCenter")} test="rte-center"><AlignCenter className="w-4 h-4" /></Btn>
          <Btn title="Align right" onClick={() => exec("justifyRight")} test="rte-right"><AlignRight className="w-4 h-4" /></Btn>

          <Sep />

          {/* Lists */}
          <Btn title="Bullet list" onClick={() => exec("insertUnorderedList")} test="rte-ul"><List className="w-4 h-4" /></Btn>
          <Btn title="Numbered list" onClick={() => exec("insertOrderedList")} test="rte-ol"><ListOrdered className="w-4 h-4" /></Btn>
          <Btn title="Link" onClick={addLink} test="rte-link"><LinkIcon className="w-4 h-4" /></Btn>
          <Btn
            title={uploading ? "Uploading…" : "Insert image"}
            onClick={triggerImageUpload}
            test="rte-image"
            active={uploading}
          >
            <ImagePlus className="w-4 h-4" />
          </Btn>
          <Btn
            title={uploading ? "Uploading…" : "Insert video"}
            onClick={triggerVideoUpload}
            test="rte-video"
            active={uploading}
          >
            <FilmIcon className="w-4 h-4" />
          </Btn>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageFile}
            className="hidden"
            data-testid="rte-image-input"
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime,video/*"
            onChange={handleVideoFile}
            className="hidden"
            data-testid="rte-video-input"
          />

          <Sep />

          <Btn title="Undo" onClick={() => exec("undo")} test="rte-undo"><Undo2 className="w-4 h-4" /></Btn>
          <Btn title="Redo" onClick={() => exec("redo")} test="rte-redo"><Redo2 className="w-4 h-4" /></Btn>
          <Btn title="Clear formatting" onClick={clearFormat} test="rte-clear"><Eraser className="w-4 h-4" /></Btn>

          <div className="ms-auto">
            <Btn
              title={preview ? "Back to edit" : "Preview"}
              onClick={() => setPreview((p) => !p)}
              test="rte-preview-toggle"
              active={preview}
            >
              <Eye className="w-4 h-4" />
              <span className="ms-1 text-[11px] uppercase tracking-wider hidden md:inline">{preview ? "Edit" : "Preview"}</span>
            </Btn>
          </div>
        </div>
      </div>

      {/* Editor body */}
      {preview ? (
        <div
          className="prose-mesc p-5 min-h-[320px] max-w-none"
          data-testid="rte-preview"
          dangerouslySetInnerHTML={{ __html: value || "<p><em>Nothing to preview yet…</em></p>" }}
        />
      ) : (
        <div className="relative">
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            onPaste={handlePaste}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            data-placeholder={placeholder}
            className={`rte-editor p-5 min-h-[320px] max-w-none outline-none focus:outline-none transition-colors ${isDragOver ? "bg-blue-50" : ""}`}
            style={{ minHeight: `${rows * 1.7}rem` }}
            dir="auto"
          />
          {isDragOver && (
            <div className="pointer-events-none absolute inset-2 border-2 border-dashed border-navy bg-white/70 grid place-items-center">
              <div className="text-center">
                <div className="text-3xl mb-2">⬇</div>
                <div className="text-[14px] font-semibold text-navy">Drop image or video here</div>
                <div className="text-[12px] text-slate600 mt-1">Will appear at this position in the article</div>
              </div>
            </div>
          )}
          <div className="px-5 py-2 border-t border-slate200 bg-slate50 text-[11px] text-slate500 leading-relaxed">
            <span className="inline-flex items-center gap-1.5 flex-wrap">
              <span className="font-semibold text-slate700">Tip:</span>
              <strong>drag &amp; drop</strong> images or videos from your computer, or copy a file and <strong>paste</strong> it with Ctrl+V — it will be uploaded automatically.
            </span>
          </div>
        </div>
      )}

      <style>{`
        .rte-editor {
          font-family: "Traditional Arabic", "Amiri", "Scheherazade New", "Noto Naskh Arabic", Georgia, serif;
          font-size: 1.1rem;
          line-height: 1.85;
        }
        .rte-editor:empty::before {
          content: attr(data-placeholder);
          color: #94A3B8;
          pointer-events: none;
        }
        .rte-editor p { margin: 0 0 0.85em 0; line-height: 1.85; }
        .rte-editor h1, .rte-editor h2, .rte-editor h3 {
          font-family: "Traditional Arabic", "Amiri", "Scheherazade New", "Playfair Display", Georgia, serif;
          color: #0F172A;
          font-weight: 700;
          margin: 1.2em 0 0.4em 0;
          line-height: 1.25;
        }
        .rte-editor h1 { font-size: 1.9rem; }
        .rte-editor h2 { font-size: 1.55rem; }
        .rte-editor h3 { font-size: 1.22rem; }
        .rte-editor strong, .rte-editor b { color: #0F172A; font-weight: 700; }
        .rte-editor em, .rte-editor i { font-style: italic; }
        .rte-editor u { text-decoration: underline; }
        .rte-editor blockquote {
          border-left: 3px solid #0F172A;
          padding-left: 1rem;
          font-style: italic;
          color: #334155;
          margin: 1em 0;
        }
        html[dir="rtl"] .rte-editor blockquote {
          border-left: none;
          border-right: 3px solid #0F172A;
          padding-left: 0;
          padding-right: 1rem;
        }
        .rte-editor ul { list-style: disc; padding-inline-start: 1.5em; margin: 0.8em 0; }
        .rte-editor ol { list-style: decimal; padding-inline-start: 1.5em; margin: 0.8em 0; }
        .rte-editor li { margin-bottom: 0.3em; }
        .rte-editor a { color: #1E3A8A; text-decoration: underline; }
        .rte-editor hr { border: none; border-top: 1px solid #E2E8F0; margin: 1.2em 0; }
        .rte-editor img { max-width: 100%; height: auto; border: 1px solid #E2E8F0; }
        .rte-editor video { max-width: 100%; height: auto; border: 1px solid #E2E8F0; background: #000; }
        .rte-editor figure { margin: 1.5em 0; }
        .rte-editor figcaption { font-size: 0.85em; color: #64748B; text-align: center; font-style: italic; margin-top: 0.5em; }
      `}</style>
    </div>
  );
}

function Btn({ children, onClick, title, test, active }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      data-testid={test}
      className={`px-1.5 h-7 inline-flex items-center justify-center rounded-sm transition-colors ${
        active ? "bg-ink text-white" : "text-slate600 hover:bg-white hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <div className="w-px h-5 bg-slate200 mx-0.5" />;
}

function ColorPicker({ label, colors, onPick, testId, highlight = false }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((o) => !o)}
        className="px-2 h-7 inline-flex items-center justify-center rounded-sm text-slate600 hover:bg-white hover:text-ink text-[13px] font-semibold"
        title={highlight ? "Highlight" : "Text color"}
        data-testid={testId}
      >
        {label}
        <span
          className="inline-block w-3 h-1 ms-1 border border-slate300"
          style={{ background: highlight ? "#FEF3C7" : "#0F172A" }}
        />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 left-0 z-20 bg-white border border-slate200 shadow-md p-2 grid grid-cols-3 gap-1 min-w-[180px]">
            {colors.map((c) => (
              <button
                key={c.value}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onPick(c.value); setOpen(false); }}
                className="flex items-center gap-2 px-2 h-7 text-[11px] text-slate700 hover:bg-slate50"
                title={c.label}
              >
                <span
                  className="w-4 h-4 border border-slate300 inline-block"
                  style={{ background: c.value === "transparent" ? "#fff" : c.value }}
                />
                {c.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
